import type { FormInstance } from "antd";
import { isProblemDetails, problemMessages, type ProblemDetails } from "@qorakul/shared/api";

/** Error thrown by mutations: carries the HTTP status and the problem details, when the API sent any. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | undefined;

  constructor(status: number, error: unknown) {
    super(String(status));
    this.name = "ApiError";
    this.status = status;
    this.problem = isProblemDetails(error) ? error : undefined;
  }

  /** Human-readable lines for an alert. */
  messages(fallback: string): string[] {
    return problemMessages(this.problem, fallback);
  }

  /** Field errors as `path -> messages`, e.g. `additionalPhones[1]`. */
  fieldErrors(): Record<string, string[]> {
    return this.problem?.errors ?? {};
  }
}

/** Converts a JSON path such as `additionalPhones[1]` or `guardians[0].phone` into an Ant Design form name path. */
export function toNamePath(path: string): (string | number)[] {
  return path
    .split(/[.[\]]+/)
    .filter((part) => part.length > 0)
    .map((part) => (/^\d+$/.test(part) ? Number(part) : part));
}

/**
 * Puts the field errors of a failed mutation on the matching inputs of an Ant Design form (paths such as
 * `additionalPhones[1]`) and returns the lines that belong to no input, or the problem title when there are no
 * field errors at all, for a summary alert.
 */
export function applyFieldErrors(form: FormInstance, err: unknown, fallback: string): string[] {
  const apiError = err instanceof ApiError ? err : undefined;
  const fieldErrors = Object.entries(apiError?.fieldErrors() ?? {});
  type NamePath = Parameters<typeof form.getFieldInstance>[0];
  const known: { name: NamePath; errors: string[] }[] = [];
  const unknown: string[] = [];
  for (const [path, errors] of fieldErrors) {
    const name = toNamePath(path) as NamePath;
    if (form.getFieldInstance(name) !== undefined) known.push({ name, errors });
    else unknown.push(...errors);
  }
  form.setFields(known);
  return fieldErrors.length === 0 ? (apiError?.messages(fallback) ?? [fallback]) : unknown;
}
